const PATCH_MARKER = "// PRODUCT_VIEWER_GTAO_HORIZON_V1";
const REGEXP_ESCAPE_REPLACEMENT = String.raw`\$&`;
const ANCHOR_WHITESPACE = String.raw`\s+`;

/**
 * Closest a horizon probe may land to the pixel it is shading, in pixels.
 *
 * GTAO estimates a horizon angle by reading the depth buffer a short distance
 * from the shaded point. Inside roughly one pixel there is nothing to read:
 * the probe lands on the same texel, so `sampleSceneViewPos - viewPos` is
 * whatever the depth quantisation left behind, and `normalize()` turns that
 * into an arbitrary direction. Some of those directions sit above the surface,
 * and the horizon only ever accumulates upward (`max(0., ...)`), so the noise
 * cannot average out — it biases every sample toward occlusion.
 *
 * A flat floor is where it shows, because every one of its probes is useless
 * in that way at once. Measured on an unoccluded plane at the project's
 * settings, open floor came back at 0.59 visibility rather than 1.0, and
 * `scale: 4` took that to 0.50 — the floor rendered at half brightness, with
 * a visible step wherever the plane ended. The same bias was darkening floor a
 * metre from a 0.6 m box, which nothing there can occlude.
 *
 * Two pixels rather than one: one is enough to reach a different texel, two
 * keeps a margin for the half-resolution AO buffer, and 1, 2 and 3 all
 * measured clean (255/255 open floor) across an 8x camera distance range and
 * at the capture canvases' smaller buffers.
 *
 * This only ever *raises* a probe distance, and only when the probe would have
 * been sub-pixel. Where a surface is close enough for `radius` to span real
 * pixels — which is where the millimetre-scale seams on a case are judged —
 * the ladder is untouched.
 *
 * Two limits worth knowing before tuning it:
 *
 * The clamp is in pixels, so it is a different world distance per buffer. At
 * 640x480 and a 3 m orbit one pixel is about 5 mm; the low-resolution preview
 * and thumbnail captures are coarser still, so they clamp away more of the
 * ladder than the interactive viewer does for the same product. Whether that
 * is visible was *not* established — the seam probe built to measure it could
 * not resolve the gap it was aiming at, so treat it as unmeasured rather than
 * as shown to be harmless.
 *
 * It is capped at `radiusToUse`. Far enough away, or on a small enough
 * buffer, two pixels exceed the configured radius; every probe then collapses
 * onto the radius itself instead of landing beyond it, so the pass keeps
 * finding occluders rather than switching off. In that regime the sub-pixel
 * bias the clamp removes can return once the radius itself spans less than
 * a pixel.
 * The camera's auto-fit on a case does not get that far away; see
 * {@link MIN_PROBE_DECLARATION}.
 */
export const MIN_PROBE_PIXELS = 2;

/**
 * Upstream's probe ladder. `sampleDir.w` is the radial jitter, `.xyz` the
 * direction; the `pow` is the `distanceExponent` ramp.
 */
const PROBE_LADDER =
  "vec3 sampleViewOffset = sampleDir.xyz * radiusToUse * sampleDir.w * " +
  "pow(float(j + 1) / float(STEPS), distanceExponent);";

/**
 * The same ladder, with the step clamped to `minProbeDistance`.
 */
const CLAMPED_PROBE_LADDER = /* glsl */ `${PATCH_MARKER}
      float probeDistance = radiusToUse * sampleDir.w *
        pow(float(j + 1) / float(STEPS), distanceExponent);
      vec3 sampleViewOffset = sampleDir.xyz *
        max(probeDistance, min(minProbeDistance, radiusToUse));`;

/**
 * Where the shaded point is resolved, and the insertion point for the clamp.
 */
const SHADED_DEPTH_ANCHOR = "vec3 viewPos = getViewPosition(vUv, depth);";

/** Upstream's own resolution uniform, and where ours is declared beside it. */
const RESOLUTION_UNIFORM = "uniform vec2 resolution;";

/**
 * {@link MIN_PROBE_PIXELS}, pre-multiplied by the ambient occlusion buffer's
 * resolution scale so that dividing by `resolution` yields the ratio for the
 * full-resolution depth buffer the probes actually read. Set by the pass in
 * `setSize`; see {@link MIN_PROBE_DECLARATION} for why it cannot be inlined.
 */
const MIN_PROBE_UNIFORM = `${RESOLUTION_UNIFORM}
uniform float minProbePixels;`;

/**
 * The clamp itself, hoisted out of the probe loops.
 *
 * `getViewPosition` at two points on the same depth plane gives the view-space
 * width of `minProbePixels` pixels at this depth, so the clamp follows the
 * camera.
 *
 * It depends on nothing but `depth` and the projection, so it belongs beside
 * the shaded point rather than in the ladder: the ladder runs
 * `DIRECTIONS * STEPS` times per pixel — 18 at the default 16 samples — and
 * two inverse-projection transforms per iteration is real arithmetic to lean
 * on a driver's loop-invariant hoisting for.
 *
 * Two things it must not do, both of which it did:
 *
 * `minProbePixels` is a uniform rather than {@link MIN_PROBE_PIXELS} inlined,
 * because `resolution` is the *ambient occlusion* buffer and that buffer is
 * scaled down while the camera moves (`setResolutionScale`). The depth this
 * probes is full resolution either way, so an inlined count made the clamp
 * twice as wide in motion as at rest — a visible change in the shading as a
 * drag started and stopped. The pass pre-multiplies the count by the scale so
 * this ratio stays the full-resolution one.
 *
 * And it is bounded by `radiusToUse`. Unbounded, far enough from the camera
 * the clamp exceeds the configured radius, every probe in the ladder lands
 * past it, nothing is ever found and the pass returns full visibility — a
 * bright patch whose edge crawls with distance and zoom. Bounded, that regime
 * degrades to "all probes at the radius" instead of switching the pass off.
 */
const MIN_PROBE_DECLARATION = /* glsl */ `${SHADED_DEPTH_ANCHOR}
      ${PATCH_MARKER}
      float minProbeDistance = abs(
        getViewPosition(
          vec2(0.5 + minProbePixels / resolution.x, 0.0), depth
        ).x - getViewPosition(vec2(0.5, 0.0), depth).x
      );`;

/** `PATCH_MARKER` occurrences a fully patched shader carries. */
const PATCH_MARKER_COUNT = 2;

/**
 * Anchors left untouched, whose meaning the patch depends on. If upstream
 * renames `resolution`, stops providing `depth` at this point, or changes what
 * `getViewPosition` returns, the clamp would compile and be wrong.
 */
const shaderInvariants = [
  { name: "resolution uniform", source: "uniform vec2 resolution;" },
  {
    name: "view position helper",
    source:
      "vec3 getViewPosition(const in vec2 screenPosition, " +
      "const in float depth) {",
  },
  { name: "shaded depth", source: SHADED_DEPTH_ANCHOR },
] as const;

function anchorPattern(anchor: string): RegExp {
  // Ignore indentation and line wrapping, but not changed GLSL expressions.
  const pattern = anchor
    .trim()
    .split(/\s+/)
    .map((token) =>
      // `\$&` expansion is the point here: it escapes the matched
      // metacharacter rather than inserting text. verbatim-replace-ok
      token.replaceAll(/[.*+?^${}()|[\]\\]/g, REGEXP_ESCAPE_REPLACEMENT),
    )
    .join(ANCHOR_WHITESPACE);
  return new RegExp(pattern, "g");
}

function requireAnchorCount(
  shader: string,
  anchor: string,
  name: string,
  expected: number,
): void {
  const pattern = anchorPattern(anchor);
  let count = 0;
  while (pattern.exec(shader) !== null) count++;
  if (count !== expected) {
    throw new Error(
      "ProductViewerGTAOPass: unsupported GTAOShader " +
        `(${name}: expected ${expected} anchor(s), found ${count}). ` +
        "Review the Three.js horizon patch before upgrading.",
    );
  }
}

/**
 * Patch an instance's fragment shader, never the shared Three.js shader.
 *
 * Both fresh and already-patched sources are validated, so a partial patch or
 * upstream drift fails loudly rather than silently changing the occlusion.
 */
export function patchGtaoHorizonShader(fragmentShader: string): string {
  const patched = fragmentShader.includes(PATCH_MARKER);
  requireAnchorCount(
    fragmentShader,
    PATCH_MARKER,
    "patch marker",
    patched ? PATCH_MARKER_COUNT : 0,
  );
  for (const { name, source } of shaderInvariants) {
    requireAnchorCount(fragmentShader, source, name, 1);
  }
  requireAnchorCount(
    fragmentShader,
    MIN_PROBE_DECLARATION,
    "probe clamp",
    patched ? 1 : 0,
  );
  requireAnchorCount(
    fragmentShader,
    MIN_PROBE_UNIFORM,
    "probe clamp uniform",
    patched ? 1 : 0,
  );
  // The offset is declared once and reused for both walks of the slice,
  // +offset and -offset, so clamping this one declaration covers both
  // horizons.
  requireAnchorCount(
    fragmentShader,
    patched ? CLAMPED_PROBE_LADDER : PROBE_LADDER,
    "probe ladder",
    1,
  );
  requireAnchorCount(
    fragmentShader,
    patched ? PROBE_LADDER : CLAMPED_PROBE_LADDER,
    "probe ladder (mixed patch versions)",
    0,
  );
  if (patched) return fragmentShader;
  // Function replacements, as in `gtaoDenoising`: a string replacement expands
  // `$&`, `$1` and `$'` out of the *inserted* GLSL rather than inserting it
  // verbatim, and these constants are built by template interpolation.
  return fragmentShader
    .replaceAll(anchorPattern(RESOLUTION_UNIFORM), () => MIN_PROBE_UNIFORM)
    .replaceAll(anchorPattern(SHADED_DEPTH_ANCHOR), () => MIN_PROBE_DECLARATION)
    .replaceAll(anchorPattern(PROBE_LADDER), () => CLAMPED_PROBE_LADDER);
}
